import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  alpha
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { APP_CONFIG } from '../../utils/constants';
import Loading from '../../components/common/Loading';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const { login, isAuthenticated, loading, error, clearError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect se já autenticado
  useEffect(() => {
    console.log('🔄 Login useEffect:', { isAuthenticated, loading, user });
    if (isAuthenticated && !loading) {
      const from = location.state?.from?.pathname || '/dashboard';
      console.log('🚀 Redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location, user]);

  // Limpar erros ao digitar
  useEffect(() => {
    if (error || localError) {
      clearError();
      setLocalError('');
    }
  }, [formData.email, formData.password, error, localError, clearError]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validações básicas
    if (!formData.email.trim()) {
      setLocalError('Email é obrigatório');
      return;
    }
    
    if (!formData.password) {
      setLocalError('Senha é obrigatória');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setLocalError('Email deve ter um formato válido');
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      console.log('📝 Submitting login with:', formData.email);
      const result = await login(formData.email, formData.password);
      console.log('📊 Login result:', result);
      
      if (result && result.success) {
        // Navegação será feita pelo useEffect
        console.log('✅ Login successful, waiting for redirect...');
      } else {
        console.log('❌ Login failed:', result);
        setLocalError(result?.error || 'Erro no login');
      }
    } catch (err) {
      setLocalError('Erro inesperado. Tente novamente.');
      console.error('💥 Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading inicial
  if (loading) {
    return <Loading fullScreen message="Verificando autenticação..." />;
  }

  // Já autenticado (evita flash)
  if (isAuthenticated) {
    return <Loading fullScreen message="Redirecionando..." />;
  }

  const displayError = localError || error;

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%' }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              background: alpha('#FFFFFF', 0.9),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha('#FFFFFF', 0.3)}`,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Header */}
            <Box textAlign="center" mb={4}>
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <img 
                    src="/Ergofy-logo-tag-azul - Copia.png" 
                    alt="Ergofy Logo" 
                    style={{
                      height: '42px',
                      width: 'auto',
                      marginBottom: '16px'
                    }}
                  />
                  <Typography 
                    variant="h6" 
                    color="text.secondary" 
                    fontWeight={500}
                    gutterBottom
                  >
                    {APP_CONFIG.SUBTITLE}
                  </Typography>
                </Box>
                <Divider sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Faça login para continuar
                  </Typography>
                </Divider>
              </motion.div>
            </Box>

            {/* Mensagem do state (se redirecionado) */}
            {location.state?.message && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  {location.state.message}
                </Alert>
              </motion.div>
            )}

            {/* Error Alert */}
            {displayError && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert 
                  severity="error" 
                  sx={{ mb: 3, borderRadius: 2 }}
                  onClose={() => {
                    setLocalError('');
                    clearError();
                  }}
                >
                  {displayError}
                </Alert>
              </motion.div>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isSubmitting}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? null : <LoginIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    position: 'relative'
                  }}
                >
                  {isSubmitting ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <LoginIcon />
                      </motion.div>
                      Entrando...
                    </Box>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </motion.div>
            </Box>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <Box mt={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Problemas para entrar?{' '}
                  <Box 
                    component="span" 
                    color="primary.main" 
                    sx={{ 
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    Entre em contato com o suporte
                  </Box>
                </Typography>
              </Box>
            </motion.div>
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Login;
